import React, {useEffect, useRef, useState} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLoading, usePersistedTransactionId } from '../../context';
import {
  HeroLogo,
  Footer,
  Button,
  Label,
  TextInput,
  FormItemContainer,
  FormContainer, SingleLineFormItemWrapper,
} from '../../components';
import AliPayPlusPaymentImage from '../../resource/figma-images/alipay-plus-payment-image.png';
import ContinueToPayImage from '../../resource/figma-images/continue-to-pay-image.svg';
import CreditCardPaymentImage from '../../resource/figma-images/credit-card-payment-image.svg';
import PayToImage from '../../resource/figma-images/PayTo_wordmark-Black.png';
import { TillPaymentInlineForm } from './TillPaymentInlineForm';
import { Alert, Stack, styled, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ReCAPTCHA from "react-google-recaptcha";
import {
  getCreditCardPaymentImage,
  getDefaultPaymentMethod,
  getDisplaySurchargeRate,
  getPageHeroLogo,
  getPageTitle,
  getReferencePlaceholder,
  getThemeColor, shouldHideAlipayPlus,
  shouldVerifyRecaptcha
} from "../../customHostConfigOnClient";
import { hexToRGBA } from "../../utils/hexToRGBA";
import {useRecaptchaReady, verifyRecaptcha} from "../../utils/useRecaptchaReady";
import {
  PAYMENT_METHOD_ALIPAY,
  PAYMENT_METHOD_CREDIT_CARD,
  PAYMENT_METHOD_INITIAL,
  PAYMENT_METHOD_PAY_ID
} from "../constants";
import {PrivacyPolicy} from "../../components/PrivacyPolicy";
import {PayIdInlineForm} from "../PayID/PayIdInlineForm";
import {renderPayIdPaymentOption} from "../PayID/payIdPaymentOption";
import {
  PaymentContent,
  PaymentDetails, PaymentHiddenWrapper,
  PaymentOption,
  PaymentOptionImage,
  PaymentOptionList,
  PaymentOptionText,
  SurchargeAmountWrapper
} from "../../components/PaymentMethod";


const InfoMessage = styled('div')`
  color: darkgray;
  text-align: center;
`;

const PaymentFormPageTitle = styled('div')`
  font-size: 15px;
  text-align: center;
  font-weight: 500;
  color: #747F94;
`;


export const PaytoPaymentForm = () => {
  const navigate = useNavigate();
  const { showLoading, hideLoading } = useLoading();

  // Hide /payto-pay page for other domains.
  if (window.location.hostname !== 'payto.oligo.com.au' && window.location.hostname !== 'localhost') {
    window.location = '/' + window.location.search;
  }

  const searchParams = new URLSearchParams(useLocation().search);
  const [billAmount, setBillAmount] = useState(searchParams.get('Amount') || '');
  const [billAmountDisabled, setBillAmountDisabled] = useState(false);
  const [userEmail, setUserEmail] = useState(searchParams.get('Email') || '');
  const [error, setError] = useState('');
  const [paymentResult, setPaymentResult] = useState('');
  const [billAmountError, setBillAmountError] = useState(false);
  const [accountID, setAccountID] = useState(searchParams.get('AccountID') || '');
  const [accountIDError, setAccountIDError] = useState(false);
  const [familyName, setFamilyName] = useState(searchParams.get('FamilyName') || '');
  const [familyNameError, setFamilyNameError] = useState(false);
  const [userContactPhone, setUserContactPhone] = useState(searchParams.get('Phone') || '');
  const tillPaymentInlineFormRef = useRef(null);
  const payIdFormRef = useRef();
  const recaptchaRef = useRef(null);
  const recaptchaReady = useRecaptchaReady();
  const persistedTransactionId = usePersistedTransactionId();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(PAYMENT_METHOD_INITIAL);

  useEffect(() => {
    if (paymentResult !== '') setSelectedPaymentMethod(PAYMENT_METHOD_INITIAL);
  }, [paymentResult]);

  const handlePaymentMethodChange =
    (paymentMethod) => () => {
      setSelectedPaymentMethod(prev => (prev !== paymentMethod ? paymentMethod : prev));
    };

  const envParam = new URLSearchParams(useLocation().search).get('env');

  const handleBillAmountChange = (value) => {
    if (isValidFloat(value)) {
      setBillAmount(value);
      setBillAmountError(false);
    }
  };

  const handleAccountIDChange = (value) => {
    setAccountID(value);
    setAccountIDError(false);
  };

  const handleFamilyNameChange = (value) => {
    setFamilyName(value);
    setFamilyNameError(false);
  };


  const isValidFloat = (value) => {
    return /^-?\d*\.?\d*$/.test(value);
  };

  const createPaymentOrderAndNavigateForAlipay = async (data) => {
    window.location.href = data.redirect_url;
  };

  const getPaymentEndpoint = (selectedPayment) => {
    switch (selectedPayment) {
      case 'wechatpay':
        return '/api/wechat/create-payment';
      case PAYMENT_METHOD_ALIPAY:
        return '/api/alipay/create-payment';
      case 'alipay-dana':
        return '/api/alipay-dana/create-payment';
      default:
        return '';
    }
  }

  const adjustCreatePaymentEndpoint = (endpoint) => {
    if (window.location.hostname === 'www.vidatennis.com.au') return `https://vida.oligo.com.au${endpoint}`;
    return endpoint;
  }

  const createPaymentOrderAndNavigate = async () => {
    if (!accountID) {
      setAccountIDError(true);
      return;
    }

    if (!familyName) {
      setFamilyNameError(true);
      return;
    }

    if (!billAmount || isNaN(parseFloat(billAmount)) || parseFloat(billAmount) <= 0) {
      setBillAmountError(true);
      return;
    }

    setBillAmountDisabled(true);

    if (selectedPaymentMethod === PAYMENT_METHOD_INITIAL) {
      setError('Please select a payment method.');
      return;
    }

    if (shouldVerifyRecaptcha() && recaptchaReady && recaptchaRef.current) {
      // Validate ReCAPTCHA
      await verifyRecaptcha(recaptchaRef, setError);
    }

    const extraPaymentParams = {
      extraParams: {
        EFN: familyName, // EXTRA_FamilyName
        ECP: userContactPhone, // EXTRA_ContactPhone
      }
    }

    try {
      if (selectedPaymentMethod === PAYMENT_METHOD_CREDIT_CARD) {
        await tillPaymentInlineFormRef.current.paymentSubmit(setPaymentResult, '/api/alipay-credit-card/create-payment-with-token', extraPaymentParams);
      } else if (selectedPaymentMethod === PAYMENT_METHOD_PAY_ID) {
        await payIdFormRef.current.submit();
      } else {
        showLoading();

        const createPaymentEndpoint = getPaymentEndpoint(selectedPaymentMethod);
        const createPaymentEndpointWithAdjustedHost = adjustCreatePaymentEndpoint(createPaymentEndpoint);
        const response = await fetch(`${createPaymentEndpointWithAdjustedHost}?env=${envParam}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            billAmount,
            billReference: accountID,
            userEmail,
            persistedTransactionId,
            ...extraPaymentParams,
          }),
        });
        const data = await response.json();
        if (data.return_code === 'SUCCESS') {
          if (selectedPaymentMethod === PAYMENT_METHOD_ALIPAY || selectedPaymentMethod === 'alipay-dana') {
            // hideLoading('Payment submit successful!', 'success');
            await createPaymentOrderAndNavigateForAlipay(data);
          }
        } else {
          hideLoading('Payment creation failed.', 'error');
        }
      }
    } catch (err) {
      hideLoading();
      setError('Create payment order failed, please try again.');
    }
  };

  const handlePayBill = async () => {
    try {
      await createPaymentOrderAndNavigate();
    } catch (err) {
      setError('Payment validation request sending failed');
      console.error(err);
    }
  };

  return (
    <FormContainer>
      <HeroLogo logo={getPageHeroLogo()} redirectPath='/payto-pay' />
      <Stack gap="20px" alignItems="center">
        <PaymentFormPageTitle>{ getPageTitle() }</PaymentFormPageTitle>
        {error && <Alert severity="error">{error}</Alert>}
        <Stack gap="20px" width="100%">
          <SingleLineFormItemWrapper>
            <FormItemContainer marginRight={'10px'}>
              <Label isRequired>Account ID</Label>
              <TextInput
                placeholder=""
                value={accountID || ''}
                onChange={handleAccountIDChange}
                error={accountIDError}
              />
            </FormItemContainer>
            <FormItemContainer>
              <Label isRequired>Family Name</Label>
              <TextInput
                placeholder=""
                value={familyName || ''}
                onChange={handleFamilyNameChange}
                error={familyNameError}
              />
            </FormItemContainer>
          </SingleLineFormItemWrapper>
          <SingleLineFormItemWrapper>
            <FormItemContainer marginRight={'10px'}>
              <Label isRequired>Payment Amount</Label>
              <TextInput
                placeholder="$"
                value={billAmount || ''}
                onChange={handleBillAmountChange}
                error={billAmountError}
                disabled={billAmountDisabled}
              />
            </FormItemContainer>
            <FormItemContainer>
              <Label>Contact Telephone No.</Label>
              <TextInput
                placeholder=""
                value={userContactPhone || ''}
                onChange={setUserContactPhone}
              />
            </FormItemContainer>
          </SingleLineFormItemWrapper>
          <SingleLineFormItemWrapper>
            <FormItemContainer>
              <Label>Receipt Email</Label>
              <TextInput
                placeholder=""
                value={userEmail || ''}
                onChange={setUserEmail}
              />
            </FormItemContainer>
          </SingleLineFormItemWrapper>

          <div>
            <Label>Payment Method</Label>
            { !getDefaultPaymentMethod() && <div style={{color: 'rgba(84, 84, 84, 0.56)', marginBottom: '15px', fontSize: '13px'}}>Please select a payment method.</div> }

            <PaymentOptionList>
              <PaymentOption
                onClick={handlePaymentMethodChange(PAYMENT_METHOD_CREDIT_CARD)}
                selected={selectedPaymentMethod === PAYMENT_METHOD_CREDIT_CARD}
                borderColor={getThemeColor()}
              >
                <PaymentOptionImage src={CreditCardPaymentImage} />
                <PaymentOptionText>Card</PaymentOptionText>
              </PaymentOption>
              { !shouldHideAlipayPlus() &&
                <PaymentOption
                  onClick={handlePaymentMethodChange(PAYMENT_METHOD_ALIPAY)}
                  selected={selectedPaymentMethod === PAYMENT_METHOD_ALIPAY}
                  borderColor={getThemeColor()}
                >
                  <PaymentOptionImage src={AliPayPlusPaymentImage} />
                  <PaymentOptionText>Alipay</PaymentOptionText>
                </PaymentOption>
              }
              { renderPayIdPaymentOption({ handlePaymentMethodChange, selectedPaymentMethod, PaymentOption, PaymentOptionImage, PaymentOptionText }) }
            </PaymentOptionList>

            { selectedPaymentMethod === PAYMENT_METHOD_CREDIT_CARD &&
              <SurchargeAmountWrapper>Surcharge: Visa or Mastercard 1.0%, AMEX 1.5%</SurchargeAmountWrapper>
            }
            { selectedPaymentMethod === PAYMENT_METHOD_ALIPAY &&
              <SurchargeAmountWrapper>Alipay Surcharge of 1.20%. Daily transfer limit of $60,000</SurchargeAmountWrapper>
            }

            <PaymentDetails>
              <PaymentHiddenWrapper show={selectedPaymentMethod === PAYMENT_METHOD_CREDIT_CARD}>
                <TillPaymentInlineForm ref={tillPaymentInlineFormRef}
                                       billAmount={billAmount}
                                       billReference={accountID}
                                       userEmail={userEmail}
                                       initLoadingFinishCallback={() => getDefaultPaymentMethod() && setSelectedPaymentMethod(getDefaultPaymentMethod())}
                                       creditCardImage={getCreditCardPaymentImage()}
                />
              </PaymentHiddenWrapper>
              <PaymentHiddenWrapper show={selectedPaymentMethod === PAYMENT_METHOD_ALIPAY}>
                <PaymentContent>
                  <img src={ContinueToPayImage} />
                  <div>Select ‘Continue to pay’ and you will be redirected to Alipay to complete payment.</div>
                </PaymentContent>
              </PaymentHiddenWrapper>
              <PaymentHiddenWrapper show={selectedPaymentMethod === PAYMENT_METHOD_PAY_ID}>
                <PayIdInlineForm
                  ref={payIdFormRef}
                  billAmount={billAmount}
                  onSubmitResult={(result) => {
                    console.log("Final result:", result);
                  }}
                  payIdMetadata={{
                    custom_field_1_label: 'Account / Debtor ID',
                    custom_field_1_value: accountID,
                    custom_field_2_label: 'Family Name / Payment Reference',
                    custom_field_2_value: familyName,
                  }}
                />
              </PaymentHiddenWrapper>
            </PaymentDetails>

            { paymentResult && <InfoMessage>{paymentResult}</InfoMessage> }

            { shouldVerifyRecaptcha() && <ReCAPTCHA
              ref={recaptchaRef}
              sitekey="6LcWNAUrAAAAAHX2haltRqZqMAMZ0qB29zv16EB8"
              size="invisible"
            /> }

            <Button $themeColor={getThemeColor()} onClick={handlePayBill} block>
              Continue to pay
            </Button>
          </div>

        </Stack>
      </Stack>
      <PrivacyPolicy />
      <Footer />
    </FormContainer>
  );
};
